import React, { Children, ReactNode } from 'react';
import styled from '@emotion/styled';
import { breakpoints, spacing } from '@naf/theme';
import { isNotTransientProp } from '../../lib/shouldForwardProp';

interface ScrollListProps {
  /** Content inside the scroll list. */
  children: ReactNode;
  className?: string;
}

/**
 * Horizontally scrolling row of equally wide items.
 *
 * Ported from the retired `@naf/cards` `ScrollList`, which has no Simba
 * counterpart: up to four items share the width on desktop, and below the `m`
 * breakpoint each item takes most of the viewport so the row scrolls.
 */
export const ScrollList = ({ children, className }: ScrollListProps) => {
  const itemCount = Children.count(children);
  const maxVisible = Math.min(itemCount, 4);

  return (
    <Wrapper className={className}>
      {Children.map(children, (child) => (
        <ScrollItem $maxVisible={maxVisible}>{child}</ScrollItem>
      ))}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  gap: 0 ${spacing.space16};
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: ${breakpoints.m}) {
    -webkit-overflow-scrolling: touch;
  }
`;

const ScrollItem = styled('div', { shouldForwardProp: isNotTransientProp })<{ $maxVisible: number }>`
  display: flex;
  flex: 0 0 ${({ $maxVisible }) => `calc(${100 / $maxVisible}% - ${spacing.space16})`};
  scroll-snap-align: start;

  > * {
    flex: 1;
    width: 100%;
  }

  @media (max-width: ${breakpoints.m}) {
    flex: 0 0 auto;
    min-width: 80%;
    max-width: 90%;
  }
`;

export default ScrollList;
